/**
 * Custom Tools & Webhooks
 * Lists the tools linked to an agent (shown inside the drawer's Advanced settings).
 */

import React from 'react';
import { Box, Typography, Button, Chip, CircularProgress } from '@mui/material';
import { Wrench, Plus, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ToolDefinition } from '../../types';

interface AgentToolsSectionProps {
  /** Undefined while the agent is not saved yet. */
  agentPublicId?: string;
  tools: ToolDefinition[];
  isLoading: boolean;
  onAddTool: () => void;
  onManage: () => void;
}

export const AgentToolsSection: React.FC<AgentToolsSectionProps> = ({
  agentPublicId,
  tools: agentTools,
  isLoading: isLoadingTools,
  onAddTool,
  onManage,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box className="mb-3 flex items-center justify-between">
        <Box className="flex items-center gap-2">
          <Wrench size={18} className="text-indigo-600 dark:text-indigo-400" />
          <Typography
            variant="subtitle1"
            className="font-semibold text-gray-900 dark:text-slate-100"
          >
            {t('tools.title', 'Custom Tools & Webhooks')}
          </Typography>
          <Chip
            size="small"
            label={agentTools.length}
            className="h-5 bg-indigo-50 text-xs text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
          />
        </Box>

        {agentPublicId && (
          <Box className="flex items-center gap-1">
            <Button
              size="small"
              startIcon={<Plus size={14} />}
              onClick={onAddTool}
              className="text-xs font-medium text-indigo-600 dark:text-indigo-400"
            >
              {t('tools.addTool', 'Add Tool')}
            </Button>
            <Button
              size="small"
              startIcon={<ExternalLink size={14} />}
              onClick={onManage}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-slate-400"
            >
              {t('tools.manage', 'Manage')}
            </Button>
          </Box>
        )}
      </Box>

      {!agentPublicId ? (
        <Typography variant="body2" className="text-xs text-gray-500 dark:text-slate-400">
          {t(
            'tools.saveAgentFirst',
            'Save this agent first to attach custom API tools and external webhooks.'
          )}
        </Typography>
      ) : isLoadingTools ? (
        <Box className="flex items-center gap-2 py-2">
          <CircularProgress size={16} />
          <Typography variant="caption" className="text-gray-500">
            {t('tools.loading', 'Loading tools...')}
          </Typography>
        </Box>
      ) : agentTools.length === 0 ? (
        <Box className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3 text-center dark:border-slate-700 dark:bg-slate-800/40">
          <Typography variant="caption" className="mb-2 block text-gray-500">
            {t('tools.noneLinked', 'No custom tools linked to this agent.')}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Plus size={14} />}
            onClick={onAddTool}
            className="text-xs"
          >
            {t('tools.createTool', 'Create Tool')}
          </Button>
        </Box>
      ) : (
        <Box className="max-h-48 space-y-1.5 overflow-y-auto pr-1">
          {agentTools.map((tool) => (
            <Box
              key={tool.publicId}
              className="flex items-center justify-between rounded border border-gray-200 bg-white p-2 text-xs dark:border-slate-700 dark:bg-slate-900"
            >
              <Box className="flex min-w-0 items-center gap-2">
                <Chip
                  size="small"
                  label={tool.method}
                  className={`h-4 font-mono text-[10px] font-bold ${
                    tool.method === 'GET'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                      : tool.method === 'POST'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                  }`}
                />
                <Typography
                  variant="caption"
                  className="max-w-[150px] truncate font-mono font-medium"
                >
                  {tool.name}
                </Typography>
                <Chip size="small" label={tool.riskLevel} className="h-4 text-[10px] capitalize" />
              </Box>
              <Typography
                variant="caption"
                className={
                  tool.status === 'active' ? 'font-medium text-emerald-600' : 'text-gray-400'
                }
              >
                {tool.status}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
